import { ArrowLeft, FileSignature } from 'lucide-react';
import { Link } from 'react-router';
import { useTdrs } from '@/api/hooks';
import type { Habilitation, TdrResume } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, EtapeBadge, PageHeader, StatutBadge, Vide } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, montant } from '@/lib/utils';

/**
 * Un TDR peut recevoir une note explicative s'il est déjà soumis ou autorisé, de la part de son émetteur ou d'un émetteur,
 * assistant ou directeur de sa direction (même règle que l'API). Un brouillon se modifie directement ; un TDR renvoyé
 * pour correction se corrige puis se soumet à nouveau (GEDEM v3.0, § 7).
 */
export const peutNoterTdr = (t: TdrResume, agentId: string, habilitations: Habilitation[]): boolean =>
  (t.statut === 'SOUMIS' || t.statut === 'AUTORISE') &&
  (t.emetteur?.id === agentId || habilitations.some((h) => (h.profil === 'EMETTEUR' || h.profil === 'ASSISTANT' || h.profil === 'DIRECTEUR') && (h.structureId === null || h.structureCode === t.structureCode)));

/** Nouvelle note explicative : choix du TDR concerné parmi les TDR soumis ou autorisés du périmètre, puis formulaire de la note. */
export function NoteNouvelle() {
  const session = useSession();
  const u = session!.utilisateur;
  const { data, isLoading, error } = useTdrs({});
  if (isLoading) return <Chargement />;
  if (error) return <ErreurBloc erreur={error} />;
  const ordre = { AUTORISE: 0, SOUMIS: 1 } as Record<string, number>;
  const eligibles = (data ?? []).filter((t) => peutNoterTdr(t, u.agentId, u.habilitations)).sort((a, b) => (ordre[a.statut] ?? 2) - (ordre[b.statut] ?? 2) || b.updatedAt.localeCompare(a.updatedAt));
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/ne" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Notes explicatives
          </Link>
        }
        titre="Nouvelle note explicative"
        sousTitre="Choisissez le TDR concerné. Une note motive un changement (dates, équipes, lieux, activités ou budget) sur un TDR déjà soumis ou autorisé ; un brouillon se modifie directement et un TDR renvoyé pour correction se corrige puis se soumet à nouveau."
      />
      {eligibles.length === 0 ? (
        <Vide message="Aucun TDR soumis ou autorisé de votre périmètre ne peut recevoir de note pour le moment." />
      ) : (
        <div className="rounded-lg border bg-card" data-testid="tdr-eligibles">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>TDR</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead className="hidden md:table-cell">Structure</TableHead>
                <TableHead className="hidden lg:table-cell">Émetteur</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="hidden sm:table-cell">Mis à jour</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {eligibles.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    <Link to={`/tdr/${t.id}`} className="font-medium text-primary hover:underline">
                      {t.numero ?? 'TDR'}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[36ch] truncate">{t.titre}</TableCell>
                  <TableCell className="hidden md:table-cell">{t.structureCode}</TableCell>
                  <TableCell className="hidden lg:table-cell">{t.emetteur?.nom}</TableCell>
                  <TableCell className="num text-right">{montant(t.montant)}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutBadge statut={t.statut} />
                    {t.statut === 'SOUMIS' && <EtapeBadge etape={t.etapeCourante} />}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{dateFr(t.updatedAt)}</TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm">
                      <Link to={`/tdr/${t.id}/note/nouvelle`}>
                        <FileSignature className="size-4" /> Rédiger la note
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
